import _ from 'lodash';
import { getActiveTab, setActiveTab } from '@/store/modules/table/active-tab';
import router from '@/routes';
import { isSettingsRouteName } from '@/routes/is-settings-route-name';
import {
  getTableQuery,
  getTableQueryTemporary,
  getTableActiveReports,
  getTableFilters,
  getTableModule,
  getTableActiveTabStack,
  setTableSelectedReport,
  setTableActiveReports,
} from '@/store/modules/table/accessors';
import { getReportColumnPresetsState } from '@/store/modules/report-columns-accessors';
import { useStore } from '@/store';

const getReportSearchSetting = () => useStore().user?.reportSearchSetting;

const getCurrentTabQuery = () => {
  if (getActiveTab() !== 0) {
    return getTableActiveReports().find(report => report.tabId === getActiveTab())?.state?.tableQuery as
      | Record<string, unknown>
      | undefined;
  }
  return getTableQuery();
};

const getCurrentTabQueryTemporary = () => {
  if (getActiveTab() !== 0) {
    return getTableActiveReports().find(report => report.tabId === getActiveTab())?.state
      ?.tableQueryTemporary as Record<string, unknown> | undefined;
  }
  return getTableQueryTemporary();
};

const persistMainTabQuery = () => {
  if (getActiveTab() === 0) {
    window.localStorage.setItem('table-query', JSON.stringify(_.omit(getTableQuery(), ['search'])));
  }
};

export const patchTableQueryField = (data: { name: string; value: unknown }) => {
  const statePath = getCurrentTabQuery();
  if (!statePath) return;

  statePath[data.name] = data.value;
  persistMainTabQuery();
};

export const patchTableQueryTemporaryField = (data: { name: string; value: unknown }) => {
  const statePath = getCurrentTabQueryTemporary();
  if (!statePath) return;

  statePath[data.name] = data.value;
};

export const patchTableQuerySearchTemporary = (data: Record<string, unknown>) => {
  if (getActiveTab() !== 0) {
    const path = getTableActiveReports().find(report => report.tabId === getActiveTab())?.state
      ?.tableQueryTemporary as Record<string, unknown> | undefined;
    if (path) {
      path.search = data;
    }
  }

  if (!getTableQueryTemporary().search) {
    getTableQueryTemporary().search = {};
  }

  if (getReportSearchSetting() === 'perTab') {
    const groupBy = getTableQuery().groupBy as string;
    if (!getTableQueryTemporary().search[groupBy]) {
      getTableQueryTemporary().search = {
        ...getTableQueryTemporary().search,
        [groupBy]: {},
      };
    }
    getTableQueryTemporary().search[groupBy] = data;
    return;
  }

  getTableQueryTemporary().search = data;
};

export const applyTableQuerySearch = () => {
  if (getActiveTab() !== 0) {
    const statePath = getTableActiveReports().find(report => report.tabId === getActiveTab())?.state as
      | {
          tableQuery: Record<string, unknown>;
          tableQueryTemporary: Record<string, unknown>;
        }
      | undefined;
    if (statePath) {
      statePath.tableQuery.search = JSON.parse(JSON.stringify(statePath.tableQueryTemporary.search));
    }
    return;
  }

  if (!getTableQuery().search) {
    getTableQuery().search = {};
  }

  if (getReportSearchSetting() === 'perTab') {
    const groupBy = getTableQuery().groupBy as string;
    if (!getTableQuery().search[groupBy]) {
      getTableQuery().search = {
        ...getTableQuery().search,
        [groupBy]: {},
      };
    }
    getTableQuery().search[groupBy] = JSON.parse(
      JSON.stringify(getTableQueryTemporary().search?.[groupBy] || {}),
    );
    return;
  }

  getTableQuery().search = JSON.parse(JSON.stringify(getTableQueryTemporary().search));
};

export const patchTableOffsetForCurrentTab = (data: { groupBy: string; value: number }) => {
  const statePath = getCurrentTabQuery();
  if (!statePath) return;

  if (typeof statePath.offset !== 'object' || statePath.offset === null) {
    statePath.offset = {};
  }
  (statePath.offset as Record<string, number>)[data.groupBy] = data.value;
  persistMainTabQuery();
};

export type TablePeriodPayload = {
  from: string;
  to: string;
  date: string;
  timezone?: string;
  compareEnabled?: boolean;
  compareFrom?: string | null;
  compareTo?: string | null;
  compareDate?: string | null;
};

export const patchTablePeriodForCurrentTab = (period: TablePeriodPayload) => {
  const statePath = getCurrentTabQuery();
  if (!statePath) return;

  statePath.from = period.from;
  statePath.to = period.to;
  statePath.date = period.date;
  statePath.timezone = period.timezone || useStore().user.defaultTimezone;
  statePath.compareEnabled = period.compareEnabled;
  statePath.compareFrom = period.compareFrom;
  statePath.compareTo = period.compareTo;
  statePath.compareDate = period.compareDate;
  persistMainTabQuery();
};

export const patchTablePeriodTemporaryForCurrentTab = (period: TablePeriodPayload) => {
  const statePath = getCurrentTabQueryTemporary();
  if (!statePath) return;

  statePath.from = period.from;
  statePath.to = period.to;
  statePath.date = period.date;
  statePath.timezone = period.timezone || useStore().user.defaultTimezone;
  statePath.compareEnabled = period.compareEnabled;
  statePath.compareFrom = period.compareFrom;
  statePath.compareTo = period.compareTo;
  statePath.compareDate = period.compareDate;
};

export const setTableView = (view: string) => {
  patchTableQueryField({ name: 'view', value: view });
};

export const setTableViewTemporary = (view: string) => {
  patchTableQueryTemporaryField({ name: 'view', value: view });
};

export const addTableColumnFilter = (data: Record<string, unknown>) => {
  const filterHaving = data.filterHaving as string | undefined;
  if (!filterHaving) return;

  let statePath = getTableFilters();
  if (getActiveTab() !== 0) {
    const report = getTableActiveReports().find(item => item.tabId === getActiveTab());
    if (!report?.state) return;
    if (!report.state.filters) {
      report.state.filters = {};
    }
    statePath = report.state.filters as Record<string, unknown>;
  }

  statePath[filterHaving] = data;
};

export const removeTableColumnFilter = (filterHaving: string) => {
  if (!filterHaving) return;

  let statePath = getTableFilters();
  if (getActiveTab() !== 0) {
    const report = getTableActiveReports().find(item => item.tabId === getActiveTab());
    if (!report?.state?.filters) return;
    statePath = report.state.filters as Record<string, unknown>;
  }

  delete statePath[filterHaving];
};

export const setTableTreeLevelsForCurrentTab = (treeLevels: string[] | null) => {
  const statePath = getCurrentTabQuery();
  if (!statePath) return;
  statePath.treeLevels = treeLevels;
  persistMainTabQuery();
};

export const setTableTreeLevelsTemporaryForCurrentTab = (treeLevels: string[] | null) => {
  const statePath = getCurrentTabQueryTemporary();
  if (!statePath) return;
  statePath.treeLevels = treeLevels ? treeLevels : null;
};

export const setTableTreeIndexForCurrentTab = (index: number, value: unknown) => {
  const statePath = getCurrentTabQuery();
  if (!statePath) return;
  if (!statePath.tree) {
    statePath.tree = new Array(2);
  }
  (statePath.tree as unknown[])[index] = value;
};

export const resetTableTreeForCurrentTab = () => {
  const statePath = getCurrentTabQuery();
  if (!statePath) return;
  statePath.tree = new Array(2);
  persistMainTabQuery();
};

export const clearTableSelectedItems = () => {
  getTableModule().selectedItems = [];
};

export const clearTableChartHiddenSeries = () => {
  getTableModule().chartHiddenSeries = {};
  window.localStorage.setItem(
    'table-chart-hidden-series',
    JSON.stringify(getTableModule().chartHiddenSeries),
  );
};

export const setTableChartOpen = (open: boolean) => {
  getTableModule().chartOpen = open;
  window.localStorage.setItem('table-chart-open', String(open));
};

export const setTableNotesOpened = (opened: boolean) => {
  getTableModule().notesOpened = opened;
};

export const setTableChartHiddenSeries = (data: { id: string; value: boolean }) => {
  getTableModule().chartHiddenSeries[data.id] = data.value;
  window.localStorage.setItem(
    'table-chart-hidden-series',
    JSON.stringify(getTableModule().chartHiddenSeries),
  );
};

export const setTableChartHiddenSeriesDashboard = (data: { id: string; value: boolean }) => {
  getTableModule().chartHiddenSeriesDashboard[data.id] = data.value;
  window.localStorage.setItem(
    'table-chart-hidden-series-dashboard',
    JSON.stringify(getTableModule().chartHiddenSeriesDashboard),
  );
};

export const replaceTableColumnsFiltersForCurrentTab = (filters: Record<string, unknown>) => {
  if (getActiveTab() !== 0) {
    const report = getTableActiveReports().find(item => item.tabId === getActiveTab());
    if (report?.state) {
      report.state.filters = { ...filters };
    }
    return;
  }

  const statePath = getTableFilters();
  Object.keys(statePath).forEach((key) => {
    delete statePath[key];
  });
  Object.assign(statePath, filters);
};

const pushTableActiveTabStack = (tabId: number) => {
  const pushCondition =
    getTableActiveTabStack()[getTableActiveTabStack().length - 1] !== tabId
    && (
      tabId <= 0
      || getTableActiveReports().some((item) => item.tabId === tabId)
    );

  if (pushCondition) {
    getTableActiveTabStack().push(tabId);
  }
};

export const navigateTableActiveTab = (data: { tabId: number; url?: string }) => {
  const tableActiveTab = isSettingsRouteName(router.currentRoute.value.name)
    ? -1
    : getActiveTab();

  pushTableActiveTabStack(tableActiveTab);

  if (getActiveTab() !== data.tabId) {
    if (getActiveTab()) {
      const currentTabIndex = getTableActiveReports().findIndex(
        (item) => item.tabId === getActiveTab(),
      );

      if (currentTabIndex !== -1) {
        const currentTab = getTableActiveReports()[currentTabIndex];

        if (data.url) {
          currentTab.url = data.url;
        }

        getTableActiveReports()[currentTabIndex] = currentTab;
      }
    }

    if (data.tabId <= 0) {
      setTableSelectedReport(null);
    }

    setActiveTab(data.tabId);
  }
};

export const popTableActiveTabStack = (closedTabId: number) => {
  let activeTabStack = getTableActiveTabStack().filter((item) => item !== closedTabId);

  if (closedTabId === getActiveTab()) {
    const nextTabId = activeTabStack.pop() || 0;
    navigateTableActiveTab({
      tabId: nextTabId,
    });
  }

  getTableModule().activeTabStack = activeTabStack;
};

export const closeTableTab = (closedTabId: number) => {
  getTableModule().activeReports = getTableActiveReports().filter(
    (item) => item.tabId !== closedTabId,
  );
  popTableActiveTabStack(closedTabId);
};

export const removeTableReportTabById = (id: string) => {
  const reports = getTableActiveReports();
  const index = reports.map((item) => item.id).indexOf(id);
  if (index !== -1) {
    reports.splice(index, 1);
  }
  if (!reports.length) {
    setTableSelectedReport(null);
  }
};

export const renameTableReportTab = (data: {
  id: string;
  name?: string;
  nameFull?: string;
}) => {
  getTableActiveReports().forEach((report) => {
    if (report.id === data.id) {
      report.title = data.nameFull || data.name || report.title;
    }

    if (report.drill) {
      (report.drill as Array<{ id?: string; name?: string; nameFull?: string }>).forEach((item) => {
        if (item.id === data.id) {
          item.name = data.name;
          item.nameFull = data.nameFull;
        }
      });
    }
  });
};

export const setTableLoading = (loading: boolean) => {
  getTableModule().loading = loading;
};

export const setTableTotal = (total: number) => {
  getTableModule().total = total;
};

export const setTableSelectedItems = (items: unknown[] | null) => {
  if (!items) {
    clearTableSelectedItems();
    return;
  }

  getTableModule().selectedItems = items;
};

export const setTablePreviewLink = (link: unknown) => {
  getTableModule().previewLink = link;
};

export const setTableNextReportTreeLevel = (level: number) => {
  getTableModule().nextReportTreeLevel = level;
};

export const setTableNextReportGroup = (group: unknown) => {
  getTableModule().nextReportGroup = group;
};

export const setTableExportParams = (params: unknown) => {
  getTableModule().exportParams = params;
};

export const setTableLastActiveTab = (value: unknown) => {
  getTableModule().lastActiveTab = value;
};

export const setTableNoReportBlockVisible = (visible: boolean) => {
  getTableModule().noReportBlockVisible = visible;
};

export const setTableProgressBlockVisible = (visible: boolean) => {
  getTableModule().progressBlockVisible = visible;
};

export const setTableIsHideFilters = (hide: boolean) => {
  getTableModule().isHideFilters = hide;
};

export const clearTableTree = () => {
  resetTableTreeForCurrentTab();
};

let nextReportTabId = 0;

export const setTableEntityName = (data: { key: string; value: unknown }) => {
  getTableModule().entityNames[data.key] = data.value;
};

export const setTableCustomGroups = (data: Record<string, unknown> | null) => {
  getTableModule().customGroups = data;
};

export const setTableDrilldownFilter = (drilldown: unknown) => {
  getTableModule().drilldownFilter = drilldown;
};

export const setTableDrilldown = (drilldown: unknown) => {
  getTableModule().drilldown = drilldown;
};

export const setTableCurrentReport = (report: unknown) => {
  getTableModule().currentReport = report;
};

export const addTableReportTab = (item: {
  drill?: unknown[];
  title?: string;
  id?: string;
  isReplaceTab?: boolean;
  isAddTab?: boolean;
  tabData?: unknown;
  state?: unknown;
  tabId?: number;
  url?: string;
  icon?: string;
  filterTables?: string;
  returnLink?: () => string;
  [key: string]: unknown;
}) => {
  if (!item.drill) return;

  const drillName = item.drill[0] as { nameFull?: string; name?: string; id?: string };
  item.title = drillName.nameFull || drillName.name || item.title;
  item.id = drillName.id;

  let index = 0;

  if (item.isReplaceTab) {
    index = getTableActiveReports().findIndex((report) => report.tabId === getActiveTab());
  } else {
    index = getTableActiveReports().findIndex(
      (report) => report.id === item.id && report.tabId === getActiveTab(),
    );
    if (index === -1) {
      index = getTableActiveReports().findIndex((report) => report.id === item.id);
    }
  }

  if (index !== -1 && !item.isAddTab) {
    item.state = getTableActiveReports()[index].state;
    item.tabId = getTableActiveReports()[index].tabId;
    delete item.isAddTab;
    delete item.tabData;

    getTableActiveReports()[index] = item;

    if (getActiveTab() !== item.tabId) {
      navigateTableActiveTab({
        tabId: item.tabId as number,
      });
    } else {
      pushTableActiveTabStack(item.tabId as number);
    }
    return;
  }

  item.state = item.tabData
    ? _.cloneDeep(item.tabData)
    : {
        filters: {},
        savedColumns: _.cloneDeep(getReportColumnPresetsState().savedColumnsInCurentPreset),
        tableQuery: {
          ...getTableQuery(),
          search: {},
          offset: {},
          treeLevels: [''],
          view: 'tree',
          tree: new Array(2),
        },
        tableQueryTemporary: {
          ...getTableQueryTemporary(),
          search: {},
          treeLevels: [''],
          view: 'tree',
          tree: new Array(2),
        },
      };

  nextReportTabId = nextReportTabId + 1;
  item.tabId = nextReportTabId;
  delete item.isAddTab;

  getTableActiveReports().push(item);

  if (!getActiveTab() && !item.tabData) {
    setActiveTab(nextReportTabId);
  } else {
    delete item.tabData;
    navigateTableActiveTab({
      tabId: nextReportTabId,
    });
  }
};

export const setTableActiveReportSavedColumns = ({
  columns,
  id,
}: {
  columns: Record<string, unknown>;
  id?: string | number;
}) => {
  const targetId = id ?? null;
  setTableActiveReports(
    getTableActiveReports().map((report) => {
      if (targetId == null || report.tabId !== targetId) {
        return report;
      }

      return {
        ...report,
        state: {
          ...(report.state ?? {}),
          savedColumns: columns,
        },
      };
    }),
  );
};
